import type { Options } from '@contentful/rich-text-react-renderer'
import { BLOCKS, INLINES } from '@contentful/rich-text-types'
import { useLocale } from '@ecomm/data-hooks'
import { useMicroCopy } from '@ecomm/micro-copy'
import { ContentfulRichText } from '@ecomm/shared-components/lib/ContentfulRichText'
import { useState } from 'react'

import { trackComparePlanClick } from '@ecomm/cdp-tracking'
import ComparisonTable, { type TableProps } from '../ComparisonTable'
import MonitoringPlanCard from '../MonitoringPlanCard'
import MonitoringPlanModal from '../MonitoringPlanModal'
import type { MonitoringPlanModalSchema } from '../MonitoringPlanModal/schema'
import { categorizeRows, mapRows } from '../utils/table'
import type { ExpandableMonitoringSchema } from './schema'

const options: Options = {
  renderNode: {
    [BLOCKS.TABLE]: node => {
      const { content } = node
      const tableData: TableProps = {
        headRows: mapRows(content, BLOCKS.TABLE_HEADER_CELL),
        bodyRows: categorizeRows(mapRows(content, BLOCKS.TABLE_CELL), [
          'MONITORING SERVICES',
          'HARDWARE & CAMERAS',
          'MOBILE APP & SMART HOME',
          'WARRANTY, DISCOUNTS & PRICE'
        ])
      }
      return <ComparisonTable {...tableData} />
    },
    [INLINES.EMBEDDED_ENTRY]: node => {
      const {
        data: { target }
      } = node
      const data: MonitoringPlanModalSchema = target

      return data.__typename === 'ContentfulMonitoringPlan' ? (
        <MonitoringPlanModal {...data} withAddToCartButton={true} />
      ) : null
    }
  }
}

type UKExpandableMonitoringProps = ExpandableMonitoringSchema & {
  readonly packageSku?: string
}

export function UKExpandableMonitoring(data: UKExpandableMonitoringProps) {
  const {
    headline,
    monitoringCards,
    comparisonTable,
    comparisonDisclaimer,
    planConfirmationModal,
    packageSku
  } = data
  const [allPlansVisible, setAllPlansVisible] = useState(false)
  const [comparisonTableVisible, setComparisonTableVisible] = useState(false)
  const microcopy = useMicroCopy()
  const locale = useLocale()

  return (
    <div className="prose md:prose-md lg:prose-lg flex w-full flex-col items-center gap-6 md:gap-8">
      {headline ? (
        <h2 className="mb-0 w-full text-center">{headline}</h2>
      ) : null}
      {monitoringCards.map((card, index) =>
        index === 0 || allPlansVisible ? (
          <MonitoringPlanCard
            key={`monitoring-card-${index}`}
            {...card}
            packageSku={packageSku}
            planConfirmationModal={planConfirmationModal}
          />
        ) : null
      )}
      {!allPlansVisible ? (
        <button
          className="btn btn-outlined btn-outlined-primary mx-auto"
          onClick={() => setAllPlansVisible(true)}
        >
          {microcopy['show-other-plans']}
        </button>
      ) : !comparisonTableVisible ? (
        <button
          className="btn btn-outlined btn-outlined-primary mx-auto"
          onClick={() => {
            trackComparePlanClick()
            setComparisonTableVisible(true)
          }}
        >
          {microcopy['compare-plans']}
        </button>
      ) : null}
      {comparisonTableVisible ? (
        <ContentfulRichText {...comparisonTable} optionsCustom={options} />
      ) : null}
      {comparisonDisclaimer && comparisonTableVisible && locale !== 'en-GB' ? (
        <div className="border-neutral-light-100 rounded-base border border-solid p-6 md:p-8">
          <ContentfulRichText
            {...comparisonDisclaimer}
            optionsCustom={options}
          />
        </div>
      ) : null}
    </div>
  )
}
